/**
 * La declaración armada: lo que el contador revisa antes de firmar.
 *
 * `GET /filings/{id}/export` ya no devuelve un «listo/no listo»: devuelve los
 * formularios del período armados por el rulepack del país, con sus casillas,
 * sus excepciones y los archivos oficiales listos para cargar en el portal.
 *
 * LO QUE ESTA CAPA VIGILA. Una declaración a la que le falta un dato no se
 * rompe: sale completa, cuadrada, y se presenta. Todo lo que hay acá existe
 * para que ninguna de esas tres cosas pase en silencio:
 *
 * * una casilla cuyo LAYOUT no está verificado contra el formulario oficial
 *   puede llevar el número correcto en el lugar equivocado;
 * * un formulario que no se pudo armar tiene que verse al lado de los que sí;
 * * y no se firma —ni se descarga para presentar— con bloqueantes abiertos.
 */

export type Severidad = "BLOCKER" | "WARNING" | "INFO"

export interface ExcepcionDeclaracion {
  severidad: Severidad
  codigo: string
  mensaje: string
  documentos: string[]
  accion: string
}

export interface CasillaDeclaracion {
  codigo: string
  etiqueta: string
  valor: number | string
  /** De qué se compone el número. Sin esto no se firma sin rehacer el trabajo. */
  origen: string | null
  documentos: string[]
  /** `false` = la POSICIÓN de la casilla no está cotejada contra el formulario. */
  layout_verificado: boolean
  notas: string
}

export interface ArchivoDeclaracion {
  nombre: string
  lineas: number
  bytes: number
  /** El PLE peruano va en latin-1. En UTF-8 el validador lo rechaza. */
  codificacion: string
  notas: string
}

export interface FormularioDeclaracion {
  codigo: string
  nombre: string
  vence: string | null
  cuadra: boolean
  total_a_pagar: number | string
  fundamento_legal: string[]
  casillas: CasillaDeclaracion[]
  archivos: ArchivoDeclaracion[]
  excepciones: ExcepcionDeclaracion[]
}

export interface Declaracion {
  periodo: string
  pais: string
  tax_id: string
  razon_social: string
  firmado_por: string | null
  soportadas: string[]
  motivo_sin_formularios: string | null
  declaraciones: FormularioDeclaracion[]
  excepciones_del_periodo: {
    severidad: Severidad
    codigo: string
    mensaje: string
    accion: string
  }[]
  listo_para_presentar: boolean
  motivo_si_no: string | null
  nota: string
}

// ---------------------------------------------------------------------------
// Qué se puede hacer, y si no, por qué
// ---------------------------------------------------------------------------

export interface Veredicto {
  puede: boolean
  /** Por qué no. Vacío cuando `puede`. */
  motivo: string
}

const ORDEN: Record<Severidad, number> = { BLOCKER: 0, WARNING: 1, INFO: 2 }

/** Todas las excepciones —del período y de cada formulario— en un solo orden. */
export function todasLasExcepciones(d: Declaracion): ExcepcionDeclaracion[] {
  const delPeriodo: ExcepcionDeclaracion[] = (d.excepciones_del_periodo ?? []).map((e) => ({
    severidad: e.severidad,
    codigo: e.codigo,
    mensaje: e.mensaje,
    documentos: [],
    accion: e.accion,
  }))
  const deFormularios = (d.declaraciones ?? []).flatMap((f) => f.excepciones ?? [])

  // Se DEDUPLICAN por código y mensaje: los avisos del libro —arrastres,
  // desglose deducido— se adjuntan a cada formulario a propósito, para que
  // quien mire uno solo los vea. Repetidos tres veces en la lista general
  // enseñan a ignorar la lista.
  const vistas = new Set<string>()
  const unicas: ExcepcionDeclaracion[] = []
  for (const e of [...delPeriodo, ...deFormularios]) {
    const clave = `${e.codigo}|${e.mensaje}`
    if (vistas.has(clave)) continue
    vistas.add(clave)
    unicas.push(e)
  }
  return unicas.sort((a, b) => ORDEN[a.severidad] - ORDEN[b.severidad])
}

export function contarPorSeveridad(excepciones: ExcepcionDeclaracion[]) {
  return {
    bloqueantes: excepciones.filter((e) => e.severidad === "BLOCKER").length,
    advertencias: excepciones.filter((e) => e.severidad === "WARNING").length,
    informativas: excepciones.filter((e) => e.severidad === "INFO").length,
  }
}

/**
 * Si este archivo se puede llevar al portal del fisco.
 *
 * NO es lo mismo que «se puede descargar». Descargar para revisar es legítimo
 * en cualquier momento —y por eso el botón no se bloquea— pero presentar con un
 * bloqueante abierto es presentar algo que el propio sistema sabe que está mal.
 * La diferencia se DICE en vez de resolverse escondiendo el botón.
 */
export function puedePresentar(d: Declaracion | null): Veredicto {
  if (!d) return { puede: false, motivo: "Todavía no se armó la declaración." }
  if (!d.declaraciones.length) {
    return {
      puede: false,
      motivo: d.motivo_sin_formularios ?? "Este país no genera formularios.",
    }
  }
  const { bloqueantes } = contarPorSeveridad(todasLasExcepciones(d))
  if (bloqueantes > 0) {
    return {
      puede: false,
      motivo: `Hay ${bloqueantes} ${bloqueantes === 1 ? "excepción bloqueante" : "excepciones bloqueantes"}: lo que saldría presentado está mal y el sistema lo sabe.`,
    }
  }
  if (!d.firmado_por) {
    return { puede: false, motivo: "Falta la firma del contador." }
  }
  return { puede: true, motivo: "" }
}

// ---------------------------------------------------------------------------
// Las casillas
// ---------------------------------------------------------------------------

/**
 * Las casillas cuya POSICIÓN no está cotejada contra el formulario oficial.
 *
 * Es una distinción que se pierde si no se nombra: el IMPORTE puede estar
 * perfectamente calculado y el número de casilla ser el de otra versión del
 * formulario. Transcrito al portal, eso declara el importe correcto en el
 * renglón equivocado — y no hay ninguna suma que se descuadre.
 */
export function casillasSinVerificar(f: FormularioDeclaracion): CasillaDeclaracion[] {
  return (f.casillas ?? []).filter((c) => !c.layout_verificado)
}

/** Un formulario de casillas se transcribe; un libro electrónico se sube. */
export function esLibroElectronico(f: FormularioDeclaracion): boolean {
  return (f.archivos ?? []).length > 0
}

/**
 * Cómo se lee el tamaño de un archivo. Un PLE de cero líneas es legal y casi
 * siempre es un error, así que el recuento va SIEMPRE visible.
 */
export function describirArchivo(a: ArchivoDeclaracion): string {
  const lineas = a.lineas === 1 ? "1 línea" : `${a.lineas} líneas`
  const kb = a.bytes < 1024 ? `${a.bytes} B` : `${(a.bytes / 1024).toFixed(1)} kB`
  return `${lineas} · ${kb} · ${a.codificacion}`
}

/** Un libro vacío merece decirse, no pasar por «0». */
export function archivoVacio(a: ArchivoDeclaracion): boolean {
  return a.lineas === 0
}
